import React, { useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Container, PageHeader, Spinner, EmptyState } from "@/components/common";
import { DanceCard } from "@/components/DanceCard";
import { LEVELS } from "@/lib/format";
import { Search, Music, TrendingUp, Lock } from "lucide-react";

export default function DanceLibrary() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("");
  const [sort, setSort] = useState(params.get("sort") === "popular" ? "popular" : "");
  const { data: countData } = useQuery({ queryKey: ["dances-count"], queryFn: async () => (await api.get("/dances/count")).data });
  const total = countData?.total;
  const { data: dances, isLoading, refetch } = useQuery({
    queryKey: ["dances", level, sort],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (level) params.set("level", level);
      if (sort) params.set("sort", sort);
      const qs = params.toString();
      return (await api.get(`/dances${qs ? `?${qs}` : ""}`)).data;
    },
  });

  const filtered = useMemo(() => {
    if (!dances) return [];
    const s = q.trim().toLowerCase();
    if (!s) return dances;
    return dances.filter((d) => [d.name, d.choreographer, d.music, d.artist].some((v) => (v || "").toLowerCase().includes(s)));
  }, [dances, q]);

  const onStatusChange = () => refetch();

  return (
    <div>
      <PageHeader eyebrow="Dance Library" title="Every dance, in one place"
        subtitle="Search our central library of line dances. Each dance links to its tutorial and step sheet — and members can save favourites." />
      <Container className="py-8">
        {!user && (
          <div data-testid="library-register-invite" className="mb-6 rounded-3xl border-2 border-[#C85A32]/30 bg-[#FBEBE4]/60 p-6 sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-[#2D1E18] sm:text-2xl"><Lock size={20} className="text-[#B04B26]" /> Unlock the full Dance Library!</h2>
                <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-[#5C4A42]">
                  Create your free LVLDC account to access dance tutorials, step sheets, playlists and your own personal dance collection.
                  {typeof total === "number" && <> <span className="font-semibold text-[#B04B26]">{total} dance{total === 1 ? "" : "s"}</span> and counting.</>}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-3">
                <Link to="/register" state={{ from: "/dances" }} data-testid="library-register-btn" className="rounded-full bg-[#C85A32] px-5 py-3 font-bold text-white hover:bg-[#B04B26]">Create Free Account</Link>
                <Link to="/login" state={{ from: "/dances" }} data-testid="library-login-btn" className="rounded-full border border-[#C85A32] px-5 py-3 font-bold text-[#B04B26] hover:bg-[#C85A32] hover:text-white">Already a Member? Log In</Link>
              </div>
            </div>
          </div>
        )}
        <div className="sticky top-[68px] z-20 -mx-4 mb-6 border-b border-border bg-[#FAF7F2]/95 px-4 py-4 backdrop-blur sm:mx-0 sm:rounded-2xl sm:border sm:px-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-xl border border-border bg-card px-3">
              <Search size={18} className="text-[#6E5B51]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="dance-search"
                placeholder={user ? "Search by name, choreographer, song or artist" : "Search by dance name"} className="w-full bg-transparent py-2.5 text-[#2D1E18] outline-none" />
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setSort(sort === "popular" ? "" : "popular")} data-testid="sort-popular"
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold ${sort === "popular" ? "bg-[#2D1E18] text-white" : "bg-card border border-border text-[#5C4A42]"}`}><TrendingUp size={15} /> Most popular</button>
              <button onClick={() => setLevel("")} data-testid="filter-level-all" className={`rounded-full px-3.5 py-2 text-sm font-semibold ${!level ? "bg-[#C85A32] text-white" : "bg-card border border-border text-[#5C4A42]"}`}>All levels</button>
              {LEVELS.map((l) => (
                <button key={l} onClick={() => setLevel(l)} data-testid={`filter-level-${l.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-full px-3.5 py-2 text-sm font-semibold ${level === l ? "bg-[#C85A32] text-white" : "bg-card border border-border text-[#5C4A42]"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        {isLoading ? <Spinner /> : filtered.length === 0 ? (
          <EmptyState title="No dances found" subtitle="Try a different search or level." icon={Music} />
        ) : (
          <>
            <p className="mb-4 text-sm text-[#6E5B51]" data-testid="dance-count">{filtered.length} dance{filtered.length === 1 ? "" : "s"}</p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((d) => <DanceCard key={d.id} dance={d} onStatusChange={onStatusChange} />)}
            </div>
          </>
        )}
      </Container>
    </div>
  );
}
